import type { GroupedGamificationRewardType } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';

export type LockBadgeKind = 'coming-soon' | 'needs-status';
export type LockBadge = { kind: 'coming-soon' } | { kind: 'needs-status'; statusName: string } | null;
export type BadgeSurface = 'statuses' | 'rewardsGrid';

type BadgeVisibility = Record<BadgeSurface, Record<LockBadgeKind, boolean>>;
type BadgeVisibilityOverrides = Partial<Record<
  GroupedGamificationRewardType,
  Partial<Record<BadgeSurface, Partial<Record<LockBadgeKind, boolean>>>>
>>;

const DEFAULT_BADGE_VISIBILITY: BadgeVisibility = {
  statuses: { 'coming-soon': true, 'needs-status': false },
  rewardsGrid: { 'coming-soon': true, 'needs-status': true },
};

// Override a badge for one benefit and surface, e.g.
// 'loss-insurance': { statuses: { 'coming-soon': false } }
export const BADGE_VISIBILITY_OVERRIDES: BadgeVisibilityOverrides = {
  'personal-slot-game': {
    statuses: { 'coming-soon': false },
  },
  'purchase-bonus': {
    statuses: { 'coming-soon': false },
  },
};

interface Params {
  statusName?: string;
  type: GroupedGamificationRewardType;
  kind: LockBadgeKind | null;
}

export const getBenefitLockBadges = (
  { type, kind, statusName }: Params,
  overrides: BadgeVisibilityOverrides = BADGE_VISIBILITY_OVERRIDES,
): Record<BadgeSurface, LockBadge> => {
  const getBadge = (surface: BadgeSurface): LockBadge => {
    if (!kind) {
      return null;
    }

    if (kind === 'needs-status' && !statusName) {
      return null;
    }

    const isVisible = overrides[type]?.[surface]?.[kind] ?? DEFAULT_BADGE_VISIBILITY[surface][kind];
    if (!isVisible) {
      return null;
    }

    if (kind === 'coming-soon') {
      return { kind: 'coming-soon' };
    }

    return statusName ? { kind: 'needs-status', statusName } : null;
  };

  return { statuses: getBadge('statuses'), rewardsGrid: getBadge('rewardsGrid') };
};
